import { NgStyle } from '@angular/common'
import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { forkJoin, Observable } from 'rxjs'

import { FabJob, FabPiece, FabProcessStep, FabSetStepApplicabilityRequest, FabStampStepRequest, ListItem, ModalEditMode, RepositoryModal } from '../../shared'

interface StepDateProps {
    stepId: string
    name: string
    colorHex: string
    actualDate: Date | null
    originalDate: Date | null
    notApplicable: boolean
    originalNotApplicable: boolean
    dateBeforeNa: Date | null
}

interface FabPieceModalParent {
    activeSteps: FabProcessStep[]
    canEdit: boolean
    jobs?: FabJob[]
    selectedJobId?: string
    syncPieceDrawings?: (pieceId: string, urls: string[], names: string[]) => void
}

@Component({
    templateUrl: './fab-piece.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle],
})
export class FabPieceComponent extends RepositoryModal<FabPiece> {
    private destroyRef = inject(DestroyRef)

    activeSteps: FabProcessStep[] = []
    stepDates: StepDateProps[] = []
    jobOptions: ListItem<string>[] = []
    scopeOptions = [
        new ListItem('Misc Metals', 'Misc Metals'),
        new ListItem('Pipe', 'Pipe'),
        new ListItem('Structural', 'Structural'),
        new ListItem('Extras', 'Extras'),
    ]
    boolList: ListItem<boolean>[] = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]
    priorityOptions: ListItem<number | null>[] = FabPieceComponent.standardPriorityOptions()
    canEdit = true
    drawings: File[] = []
    progress = 0
    faX = faX

    private static readonly IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tif', '.tiff']

    private static standardPriorityOptions(): ListItem<number | null>[] {
        const options: ListItem<number | null>[] = [new ListItem<number | null>(null, '')]
        for (let n = 1; n <= 10; n++) {
            options.push(new ListItem<number | null>(n, String(n)))
        }
        return options
    }

    /** Blank and 1–10. An existing value outside that range stays visible so the dialog does not look unset. */
    private static priorityOptionsFor(current: number | null | undefined): ListItem<number | null>[] {
        const options = FabPieceComponent.standardPriorityOptions()
        if (current != null && (current < 1 || current > 10)) {
            options.push(new ListItem<number | null>(current, String(current)))
        }
        return options
    }

    protected onInitialized(): void {
        const parent = this.editOptions.initializeParams as FabPieceModalParent
        this.activeSteps = [...(parent?.activeSteps ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
        this.canEdit = parent?.canEdit !== false
        this.jobOptions = (parent?.jobs ?? []).map(j => new ListItem(j.id, j.displayName || j.jobDescription || j.name || ''))
        this.target.isStock = this.target.isStock ?? false
        this.target.isRework = this.target.isRework ?? false
        this.target.priority = this.target.priority ?? null
        this.priorityOptions = FabPieceComponent.priorityOptionsFor(this.target.priority)
        this.target.imageUrls ??= []
        this.target.imageFileNames ??= []

        if (this.isAdding || this.isDuplicating) {
            if (!this.target.id) {
                this.target.id = crypto.randomUUID()
            }
            if (!this.target.quantity) {
                this.target.quantity = 1
            }
            if (!this.target.status) {
                this.target.status = 'Active'
            }
            if (!this.target.scope) {
                this.target.scope = 'Misc Metals'
            }
            if (!this.target.fabJobId && parent?.selectedJobId) {
                this.target.fabJobId = parent.selectedJobId
            }
            this.target.quantityShipped = this.target.quantityShipped ?? 0
            this.target.isRework = this.target.isRework ?? false
            this.target.isStock = this.target.isStock ?? false
        }

        if (this.isDuplicating) {
            this.target.imageUrls = []
            this.target.imageFileNames = []
        }

        const isNewPiece = this.isAdding || this.isDuplicating
        this.stepDates = this.activeSteps.map(step => {
            const existing = (this.target.steps || []).find(s => s.fabProcessStepId === step.id)
            const actual = existing?.actualDate ? new Date(existing.actualDate) : null
            const notApplicable = !!existing?.isNotApplicable
            return {
                stepId: step.id,
                name: step.name,
                colorHex: step.colorHex,
                actualDate: notApplicable ? null : actual,
                originalDate: actual,
                notApplicable,
                originalNotApplicable: isNewPiece ? false : notApplicable,
                dateBeforeNa: null,
            }
        })
    }

    onNotApplicableChanged(sd: StepDateProps): void {
        if (sd.notApplicable) {
            sd.dateBeforeNa = sd.actualDate
            sd.actualDate = null
            return
        }
        if (sd.dateBeforeNa) {
            sd.actualDate = sd.dateBeforeNa
            sd.dateBeforeNa = null
        }
    }

    /** Copies a newly entered later-step date into earlier blank, applicable steps. */
    onStepDateChanged(sd: StepDateProps): void {
        if (!sd.actualDate || sd.notApplicable) return
        const current = sd.actualDate instanceof Date ? sd.actualDate : new Date(sd.actualDate)
        if (Number.isNaN(current.getTime())) return
        const original = sd.originalDate ? sd.originalDate.toDateString() : null
        if (original === current.toDateString()) return

        const index = this.stepDates.indexOf(sd)
        if (index <= 0) return
        for (let i = 0; i < index; i++) {
            const earlier = this.stepDates[i]
            if (earlier.notApplicable || earlier.actualDate) continue
            earlier.actualDate = new Date(current.getTime())
        }
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void): void {
        const stampChangedDates = (saved: FabPiece) => {
            const calls: Observable<unknown>[] = this.stepDates
                .filter(sd => {
                    if (sd.notApplicable) return false
                    const orig = sd.originalDate ? sd.originalDate.toDateString() : null
                    const curr = sd.actualDate ? sd.actualDate.toDateString() : null
                    return orig !== curr
                })
                .map(sd => {
                    const body: FabStampStepRequest = {
                        fabPieceId: saved.id,
                        fabProcessStepId: sd.stepId,
                        actualDate: sd.actualDate,
                        clear: !sd.actualDate,
                        note: null,
                    }
                    return this.services.data.post<FabPiece>('api/FabBoard/StampStep', body)
                })

            if (this.drawings.length > 0) {
                calls.push(this.uploadDrawings(saved.id))
            }

            if (calls.length === 0) {
                resolveSave(saved)
                return
            }

            forkJoin(calls)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: results => resolveSave(results[results.length - 1] ?? saved),
                    error: err => {
                        this.onError(err)
                        rejectSave(err)
                    },
                })
        }

        const afterPieceSave = (saved: FabPiece) => {
            const applicability = this.stepDates.filter(sd => sd.notApplicable !== sd.originalNotApplicable)
            if (applicability.length === 0) {
                stampChangedDates(saved)
                return
            }

            const body = new FabSetStepApplicabilityRequest()
            body.fabPieceId = saved.id
            body.steps = applicability.map(sd => ({
                fabProcessStepId: sd.stepId,
                isNotApplicable: sd.notApplicable,
            }))
            this.services.data
                .post<FabPiece>('api/FabBoard/SetStepApplicability', body)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: updated => stampChangedDates(updated ?? saved),
                    error: err => {
                        this.onError(err)
                        rejectSave(err)
                    },
                })
        }

        const observable =
            this.editOptions.mode === ModalEditMode.adding || this.editOptions.mode === ModalEditMode.duplicating
                ? this.repositoryAdd()
                : this.repositoryEdit()

        observable.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: result => afterPieceSave(result),
            error: err => {
                this.onError(err)
                rejectSave(err)
            },
        })
    }

    isImageUrl(url: string): boolean {
        const lower = url.toLowerCase()
        return FabPieceComponent.IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext))
    }

    isPdf(url: string): boolean {
        return url.toLowerCase().endsWith('.pdf')
    }

    getExistingImageSrc(url: string) {
        return './Resources/Images/FabPiece/' + url + '?w=300'
    }

    pendingPreviewUrl(file: File): string | null {
        return (file as File & { url?: string }).url ?? null
    }

    displayFileName(index: number): string {
        const names = this.target.imageFileNames
        const fromApi = names?.[index]
        if (fromApi) return fromApi
        const url = this.target.imageUrls[index]
        return url?.split('/').pop() ?? ''
    }

    openDrawing(url: string) {
        if (this.isImageUrl(url)) {
            window.open('./Resources/Images/FabPiece/' + url, '_blank')
            return
        }
        const docId = FabPieceComponent.documentIdFromStoredUrl(url)
        if (docId) {
            window.open(`api/FabPiece/DownloadDocument?documentId=${encodeURIComponent(docId)}`, '_blank')
            return
        }
        window.open('./Resources/Images/FabPiece/' + url, '_blank')
    }

    async onDrawingChanged(event: Event) {
        const input = event.target as HTMLInputElement
        if (!input.files) return
        const files = Array.from(input.files)
        input.value = ''

        for (const file of files) {
            if (this.isImageFile(file)) {
                const options: Options = {
                    maxSizeMB: 2,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    onProgress: progress => {
                        this.progress = progress
                    },
                }
                const compressedFile = await imageCompression(file, options)
                ;(compressedFile as File & { url?: string }).url = await imageCompression.getDataUrlFromFile(compressedFile)
                this.drawings.push(compressedFile)
            } else {
                this.drawings.push(file)
            }
        }
        this.progress = 0
    }

    removeDrawing(file: File) {
        const idx = this.drawings.indexOf(file)
        if (idx > -1) this.drawings.splice(idx, 1)
    }

    removeExistingDrawing(url: string) {
        const imageId = FabPieceComponent.documentIdFromStoredUrl(url)
        if (!imageId) return

        this.services.data.http
            .delete(`api/FabPiece/DeleteImage?imageId=${encodeURIComponent(imageId)}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    const urls = this.target.imageUrls ?? []
                    const idx = urls.indexOf(url)
                    this.target.imageUrls = urls.filter(u => u !== url)
                    if (idx > -1) {
                        this.target.imageFileNames = (this.target.imageFileNames ?? []).filter((_, i) => i !== idx)
                    }
                    const parent = this.editOptions.initializeParams as FabPieceModalParent
                    parent?.syncPieceDrawings?.(this.target.id, this.target.imageUrls, this.target.imageFileNames)
                },
                error: errorResult => this.onError(errorResult),
            })
    }

    private uploadDrawings(id: string): Observable<unknown> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.drawings.length; i++) {
            uploadData.append('myFile' + i, this.drawings[i], this.drawings[i].name)
        }
        return this.services.data.post('api/FabPiece/UploadImages', uploadData)
    }

    private isImageFile(file: File): boolean {
        if (file.type?.startsWith('image/')) return true
        const name = file.name.toLowerCase()
        return FabPieceComponent.IMAGE_EXTENSIONS.some(ext => name.endsWith(ext))
    }

    private static documentIdFromStoredUrl(url: string): string | null {
        const parts = url.split('/').filter(Boolean)
        if (parts.length < 2) return null
        const file = parts[1]
        const dot = file.lastIndexOf('.')
        const idPart = dot > 0 ? file.slice(0, dot) : file
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idPart) ? idPart : null
    }
}
